import { computed, reactive } from "vue";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useModelViewStore } from "@editor/views/model/modelView.store";
import { normalizeIdentity } from "@/helpers/utility/Id";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import { isSceneElementNode } from "../sceneElement/sceneElementFunctions";
import { useElements } from "../element/elements";
import { notNull } from "@/helpers/utility/Type";

export const useFlowBuilder = function () {
  const selectionStore = useSelectionStore();
  const targetStore = useTargetStore();
  const resourceStore = useResourceStore();
  const indexStore = useIndexStore();
  const sceneNodeStore = useSceneNodesStore();
  const modelViewStore = useModelViewStore();
  const elements = useElements();

  const targets = targetStore.items;
  const resources = resourceStore.items;
  const indices = indexStore.items;

  const items = {
    target: targets,
    resource: resources,
    index: indices
  };

  const baseElement = computed(() => {
    if (modelViewStore.flowSourceElement) {
      const { id, type } = normalizeIdentity(modelViewStore.flowSourceElement);
      return items[type][id];
    }

    const selectedItems = selectionStore.selectedItems.filter(
      (i) => i.type == "target" || i.type == "resource"
    ) as { type: "target" | "resource"; id: number }[];

    const selectedItem = selectedItems?.[0];
    if (selectedItem == null) return null;

    return items[selectedItem.type][selectedItem.id];
  });

  const selectedElements = computed(() => {
    const selectedTargets = selectionStore.selected.target || [];
    const targetIds = selectedTargets.filter(
      (id) => id != baseElement.value?.id || baseElement.value.type != "target"
    );
    const targets = targetStore.getArrayByIds(targetIds);
    
    const selectedResources = selectionStore.selected.resource || [];
    const resourceIds = selectedResources.filter(
      (id) => id != baseElement.value?.id || baseElement.value.type != "resource"
    );
    const resources = resourceStore.getArrayByIds(resourceIds);

    const selectedSceneNodeIds = selectionStore.selected.sceneNode || [];
    const sceneNodes = sceneNodeStore.getArrayByIds(selectedSceneNodeIds).filter(isSceneElementNode);
    const nodeElements = sceneNodes.map((node) => elements.byUid[node.data.elementUid]).filter(notNull).filter((element) => element.type != "index");

    return [...targets, ...resources, ...nodeElements];
  });

  const endElement = computed(() => {
    if (selectedElements.value.length === 1) return selectedElements.value[0];
    return null;
  });

  function exitFlowBuilder() {
    modelViewStore.flowSourceElement = null;
  }

  return reactive({
    baseElement,
    endElement,
    exitFlowBuilder
  });
};
